import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getUserInfo,
  patchUserName,
  confirmOldPass,
  getNotiSetList,
  patchNotiSetList,
  patchMarketingAgree,
} from "api/user";

const initialState = {
  userInfo: {},
  notificationSettings: {},
  fetchUserLoading: false,
  fetchUserError: null,
};

export const fetchUserInfo = createAsyncThunk(
  "user/fetchUserInfo",
  async (payload) => {
    return await getUserInfo();
  }
);

export const updateUserInfo = createAsyncThunk(
  "user/updateUserInfo",
  async (payload) => {
    return await patchUserName(payload);
  }
);

export const confirmOldPasscode = createAsyncThunk(
  "user/confirmOldPasscode",
  async (payload) => {
    return await confirmOldPass(payload);
  }
);

export const updateMarketingAgreeSettings = createAsyncThunk(
  "user/updateMarketingAgreeSettings",
  async (updatedSetting, { dispatch }) => {
    const { value } = updatedSetting;
    const res = await patchMarketingAgree(value);
    if (res) {
      const updatedUserInfo = await dispatch(fetchUserInfo()).unwrap();
      return updatedUserInfo;
    }
    throw new Error("Failed to update marketing agreement settings");
  }
);

const user = createSlice({
  name: "user",
  initialState,
  reducers: {
    fetchUserStart: (state) => {
      state.fetchUserLoading = true;
    },
    fetchUserSuccess: (state, action) => {
      state.userInfo = action.payload;
      state.fetchUserLoading = false;
      state.fetchUserError = null;
    },

    setNotificationSettings: (state, action) => {
      state.notificationSettings = action.payload;
    },

    fetchNotificationSettingsSuccess: (state, action) => {
      state.notificationSettings = action.payload;
      state.fetchUserLoading = false;
      state.fetchUserError = null;
    },

    fetchUserFail: (state, action) => {
      state.userInfo = null;
      state.fetchUserLoading = false;
      state.fetchUserError = action.payload;
    },
    resetUser: () => initialState,
    resetReduxState: () => {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.userInfo = action.payload;
      })
      .addCase(updateUserInfo.fulfilled, (state, action) => {
        if (action.payload === 200) {
          const { name } = action.meta.arg;
          state.userInfo = { ...state.userInfo, name: name };
        }
      })
      .addCase(confirmOldPasscode.fulfilled, (state, action) => {
        if (action.payload) {
          const { passCode } = action.meta.arg;
          state.userInfo = { ...state.userInfo, oldPassCode: passCode };
        }
      })
      .addCase(updateMarketingAgreeSettings.fulfilled, (state, action) => {
        state.userInfo = action.payload;
      });
  },
});

export const {
  fetchUserStart,
  fetchUserSuccess,
  setNotificationSettings,
  fetchNotificationSettingsSuccess,
  fetchUserFail,
  resetUser,
  resetReduxState,
} = user.actions;

export const fetchNotificationSettings = () => async (dispatch) => {
  try {
    dispatch(fetchUserStart());
    getNotiSetList()
      .then((data) => {
        const res = data.reduce((acc, cur) => {
          if (cur.alarmType === "MANAGER_ALARM_COACH") {
            acc["coach"] = cur.lever === "ON" ? true : false;
            acc["coachValue"] = cur.seqManagerAlarmSetting;
          }
          if (cur.alarmType === "MANAGER_ALARM_MEMBER") {
            acc["user"] = cur.lever === "ON" ? true : false;
            acc["userValue"] = cur.seqManagerAlarmSetting;
          }
          if (cur.alarmType === "MANAGER_ALARM_SALES") {
            acc["sales"] = cur.lever === "ON" ? true : false;
            acc["salesValue"] = cur.seqManagerAlarmSetting;
          }
          if (cur.alarmType === "MANAGER_ALARM_SCHEDULE") {
            acc["schedule"] = cur.lever === "ON" ? true : false;
            acc["scheduleValue"] = cur.seqManagerAlarmSetting;
          }
          if (cur.alarmType === "MANAGER_ALARM_CEO_REPORT") {
            acc["ceoReport"] = cur.lever === "ON" ? true : false;
            acc["ceoReportValue"] = cur.seqManagerAlarmSetting;
          }
          if (cur.alarmType === "MANAGER_ALARM_SMS_NOTICE") {
            acc["sms"] = cur.lever === "ON" ? true : false;
            acc["smsValue"] = cur.seqManagerAlarmSetting;
          }
          return acc;
        }, {});
        dispatch(fetchNotificationSettingsSuccess(res));
      })
      .catch((e) => {});
  } catch (error) {
    dispatch(fetchUserFail(error.message));
    console.error(error);
  }
};

export const updateNotificationSettings =
  (updatedSetting) => async (dispatch, getState) => {
    try {
      const { name, value, checked } = updatedSetting;
      const res = await patchNotiSetList(value, checked);
      if (res) {
        const newSettings = {
          ...getState().user.notificationSettings,
          [name]: checked,
        };
        dispatch(setNotificationSettings(newSettings));
      }
    } catch (error) {
      console.error(error);
    }
  };

export default user.reducer;
